// @ts-nocheck
import { request as requester, useQueriesFetch, useQueryFetch } from 'sonata-react-components';
import { ALL_SUMMARY_ITEM_KEYS } from 'sonata-static-storage/lib/samples/statsPreferences.samples';

import { getTimeFrameForCustomTime } from '@/helpers/reporting.helpers';
import { useReportingFilters } from '@/ts-modules/reporting/reporting.hooks';

import type {
  PaginatedStatsOptions,
  PaginatedStatsParams
} from 'sonata-platform-components/lib/types/types/reporting.types';
import type {
  QueriesFetchResponse,
  QueryFetchOptions,
  QueryFetchResponse
} from 'sonata-react-components/src_ts/hooks/useQueryFetch';
import type { CampaignProgressAndGoals, PaginatedStatsResult } from 'sonata-static-storage/lib/common/common.types';
import type { SummaryItemKey } from '@/ts-contexts/reporting/Summary/SummaryContext';

type AnalyticalViewTimeFrameUnit = 'hours' | 'days';
type AnalyticalViewTimeFrameUnitOfTime = 'hour' | 'day';

type AnalyticalViewTimeFrame = {
  format: string;
  subtraction?: {
    quantity: number;
    unit: AnalyticalViewTimeFrameUnit;
  };
  timeFrame: number;
  unit: AnalyticalViewTimeFrameUnit;
  unitOfTime: AnalyticalViewTimeFrameUnitOfTime;
};

export const ANALYTICAL_VIEW_TIMEFRAME: {
  campaignByDays: AnalyticalViewTimeFrame;
  days: AnalyticalViewTimeFrame;
  hours: AnalyticalViewTimeFrame;
} = {
  campaignByDays: {
    format: 'YYYY-MM-DDTHH:mm:ss',
    timeFrame: 10,
    unit: 'days',
    unitOfTime: 'day'
  },
  days: {
    format: 'YYYY-MM-DDTHH:mm:ss',
    subtraction: {
      quantity: 25,
      unit: 'hours'
    },
    timeFrame: 10,
    unit: 'days',
    unitOfTime: 'day'
  },
  hours: {
    format: 'YYYY-MM-DDTHH:mm:ss',
    subtraction: {
      quantity: 1,
      unit: 'hours'
    },
    timeFrame: 24,
    unit: 'hours',
    unitOfTime: 'hour'
  }
};

export function useFetchPaginatedStats(
  userId: string,
  campaignIds: string[],
  params: Partial<PaginatedStatsParams>,
  options: PaginatedStatsOptions | undefined,
  requestToken: Record<string, any>,
  useQueryOptions: QueryFetchOptions<PaginatedStatsResult>,
  onSuccessCallback: (result: PaginatedStatsResult) => void
): QueryFetchResponse<PaginatedStatsResult> {
  const cacheKey = ['paginatedStats', userId, campaignIds, params, options];
  return useQueryFetch<PaginatedStatsResult>(
    cacheKey,
    async () => {
      const result = await requester.makeRequest(
        `${taptap.config.backendUrlBase}report/paginatedStats`,
        {
          data: {
            params,
            ...(options && { opts: options })
          },
          method: 'POST',
          params: {
            campaignIds: campaignIds.join(','),
            userId
          }
        },
        requestToken
      );
      onSuccessCallback(result);
      return result;
    },
    {
      ...useQueryOptions,
      enabled: Boolean(useQueryOptions?.enabled ?? true),
      staleTime: 0
    }
  );
}

export function useMultipleFetchPaginatedStats(
  userId: string,
  campaignIds: string[],
  params: PaginatedStatsParams,
  options: PaginatedStatsOptions | undefined,
  requestToken: Record<string, any>,
  useQueryOptions: Omit<QueryFetchOptions<PaginatedStatsResult>, 'onSuccess'>,
  onSuccessCallback: (result: PaginatedStatsResult, cid: string) => void
): QueriesFetchResponse<PaginatedStatsResult[]> {
  return useQueriesFetch<PaginatedStatsResult[]>(
    campaignIds.map(cid => {
      const cacheKey = ['paginatedStats', userId, cid, params, options];
      return {
        ...useQueryOptions,
        enabled: Boolean(useQueryOptions?.enabled ?? true),
        queryFn: async (): Promise<PaginatedStatsResult> => {
          const result = await requester.makeRequest(
            `${taptap.config.backendUrlBase}report/paginatedStats`,
            {
              data: {
                params,
                ...(options && { opts: options })
              },
              method: 'POST',
              params: {
                campaignIds: cid,
                userId
              }
            },
            requestToken
          );
          onSuccessCallback(result, cid);
          return result;
        },
        queryKey: cacheKey
      };
    })
  );
}

export function useSummaryGlobalStats(
  userId: string,
  cids: string[],
  defaultCurrency: string,
  token: Record<string, any>,
  updateStateCallback: (result: PaginatedStatsResult) => void,
  enabled = true
): QueryFetchResponse<PaginatedStatsResult> {
  const { currency: currencyFilter, ...filters } = useReportingFilters({ section: 'summary' });
  const currency = currencyFilter || defaultCurrency;
  return useFetchPaginatedStats(
    userId,
    cids,
    {
      groupOptions: [
        {
          field: 'cid',
          from: 0,
          others: false,
          size: cids.length
        }
      ],
      outputMetrics: ALL_SUMMARY_ITEM_KEYS,
      ...filters
    },
    {
      currencyOptions: {
        currency
      }
    },
    token,
    {
      enabled,
      retry: 0
    },
    updateStateCallback
  );
}

export function useSummaryDOOHPatrimonyStats(
  userId: string,
  cids: string[],
  defaultCurrency: string,
  summaryItemsKeys: SummaryItemKey[],
  token: Record<string, any>,
  updateStateCallback: (result: PaginatedStatsResult) => void
): QueryFetchResponse<PaginatedStatsResult> {
  const { currency: currencyFilter, ...filters } = useReportingFilters({ section: 'summary' });
  const currency = currencyFilter || defaultCurrency;
  return useFetchPaginatedStats(
    userId,
    cids,
    {
      groupOptions: [
        {
          field: 'screenId',
          from: 0,
          others: true,
          size: 3000
        },
        {
          field: 'cid',
          from: 0,
          others: true,
          size: cids.length
        }
      ],
      outputMetrics: ['playouts', 'impressions'],
      ...filters
    },
    {
      currencyOptions: {
        currency
      }
    },
    token,
    {
      enabled: summaryItemsKeys.includes('doohPatrimony'),
      retry: 0
    },
    updateStateCallback
  );
}

export function useAnalyticalViewDayStats(
  userId: string,
  cids: string[],
  defaultCurrency: string,
  analyticalViewPermission: boolean,
  token: Record<string, any>,
  updateStateCallback: (result: PaginatedStatsResult) => void
): QueryFetchResponse<PaginatedStatsResult> {
  const { currency: currencyFilter, ...filters } = useReportingFilters({ section: 'summary' });
  const currency = currencyFilter || defaultCurrency;
  return useFetchPaginatedStats(
    userId,
    cids,
    {
      groupOptions: [
        {
          field: 'day',
          from: 0,
          others: false,
          size: ANALYTICAL_VIEW_TIMEFRAME.days.timeFrame
        }
      ],
      outputMetrics: ALL_SUMMARY_ITEM_KEYS,
      ...filters,
      dateInterval: {
        ...filters.dateInterval,
        ...getTimeFrameForCustomTime(
          filters.dateInterval,
          ANALYTICAL_VIEW_TIMEFRAME.days,
          filters.dateInterval.timezone
        )
      },
      sortOptions: {
        metric: 'day',
        order: 'desc'
      }
    },
    {
      currencyOptions: {
        currency
      }
    },
    token,
    {
      enabled: analyticalViewPermission,
      retry: 0
    },
    updateStateCallback
  );
}

export function useAnalyticalViewHourStats(
  userId: string,
  cids: string[],
  defaultCurrency: string,
  analyticalViewPermission: boolean,
  token: Record<string, any>,
  updateStateCallback: (result: PaginatedStatsResult) => void
): QueryFetchResponse<PaginatedStatsResult> {
  const { currency: currencyFilter, ...filters } = useReportingFilters({ section: 'summary' });
  const currency = currencyFilter || defaultCurrency;
  return useFetchPaginatedStats(
    userId,
    cids,
    {
      groupOptions: [
        {
          field: 'hour',
          from: 0,
          others: false,
          size: ANALYTICAL_VIEW_TIMEFRAME.hours.timeFrame
        }
      ],
      outputMetrics: ALL_SUMMARY_ITEM_KEYS,
      ...filters,
      dateInterval: {
        ...filters.dateInterval,
        ...getTimeFrameForCustomTime(
          filters.dateInterval,
          ANALYTICAL_VIEW_TIMEFRAME.hours,
          filters.dateInterval.timezone
        )
      },
      sortOptions: {
        metric: 'hour',
        order: 'desc'
      }
    },
    {
      currencyOptions: {
        currency
      }
    },
    token,
    {
      enabled: analyticalViewPermission,
      retry: 0
    },
    updateStateCallback
  );
}

export function useAnalyticalCampaignViewByDayStats(
  userId: string,
  cids: string[],
  defaultCurrency: string,
  token: Record<string, any>,
  updateStateCallback: (result: PaginatedStatsResult, cid: string) => void
): QueriesFetchResponse<PaginatedStatsResult[]> {
  const { currency: currencyFilter, ...filters } = useReportingFilters({ section: 'summary' });
  const currency = currencyFilter || defaultCurrency;
  return useMultipleFetchPaginatedStats(
    userId,
    cids,
    {
      groupOptions: [
        {
          field: 'day',
          from: 0,
          others: false,
          size: 10
        }
      ],
      outputMetrics: ALL_SUMMARY_ITEM_KEYS,
      ...filters,
      dateInterval: {
        ...filters.dateInterval,
        ...getTimeFrameForCustomTime(
          filters.dateInterval,
          ANALYTICAL_VIEW_TIMEFRAME.campaignByDays,
          filters.dateInterval.timezone
        )
      },
      sortOptions: {
        metric: 'day',
        order: 'desc'
      }
    },
    {
      currencyOptions: {
        currency
      }
    },
    token,
    {
      enabled: true,
      retry: 0
    },
    updateStateCallback
  );
}

function getReportingDetailedStatsUserId(userId: string, campaignIds: string[], token: Record<string, any>) {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/detailedStats/${userId}`,
    {
      method: 'POST',
      params: {
        campaignIds,
        format: 'none'
      }
    },
    token
  );
}

export function useCampaignDetailStats(
  userId: string,
  campaignIds: string[],
  token: Record<string, any>,
  detailStatsPermission: boolean,
  updateStateCallback: (results: CampaignProgressAndGoals[]) => void
): QueryFetchResponse<CampaignProgressAndGoals[]> {
  const cacheKey = ['detailStats', userId, campaignIds];
  return useQueryFetch<CampaignProgressAndGoals[]>(
    cacheKey,
    async () => {
      const result = await getReportingDetailedStatsUserId(userId, campaignIds, token);
      updateStateCallback(result);
      return result;
    },
    {
      enabled: detailStatsPermission,
      retry: 0
    }
  );
}
